import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MarketplacePaymentsAdminService } from '../../core/services/marketplace-payments-admin.service';
import {
  Dispute,
  MarketplacePayment,
  MarketplacePaymentRevision,
  DisputeReasonCode,
} from '../../core/models/marketplace-payment.model';
import { ToastService } from '../../core/services/toast.service';

/**
 * Dispute detail page.
 *
 * Spec: /agent-os/specs/2026-06-10-deferred-capture-admin-approval/spec.md (§ 5.3.1a, § 8.5)
 *
 * Sections:
 *   1. Header — dispute id, reason, current status, who filed.
 *   2. Description card — the farmer's full description.
 *   3. Linked payment summary — quick context on the money at stake.
 *   4. Resolution panel (visible when status='open'):
 *        - Resolution type radio: farmer_wins / pilot_wins / split
 *        - Refund amount field (visible only for split)
 *        - Resolution notes
 *        - Resolve button
 *   5. Resolution history (visible when status starts with 'resolved'):
 *        - Read-only display of the prior resolution
 *
 * Inline panel rather than a modal because the resolution involves
 * structured input (radio + amount + notes) + decisions tend to be
 * deliberate, not snap. Inline keeps the dispute context visible while
 * the admin types.
 */
@Component({
  selector: 'app-dispute-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './dispute-detail.component.html',
})
export class DisputeDetailComponent implements OnInit {
  disputeId = '';
  dispute: Dispute | null = null;
  payment: MarketplacePayment | null = null;
  revisions: MarketplacePaymentRevision[] = [];
  loading = true;
  loadError: string | null = null;

  // Resolution form
  resolutionType: 'farmer_wins' | 'pilot_wins' | 'split' | null = null;
  refundAmount: number | null = null;
  resolutionNotes = '';
  processing = false;
  submitError: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: MarketplacePaymentsAdminService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.disputeId = this.route.snapshot.paramMap.get('id') || '';
    this.reload();
  }

  reload(): void {
    this.loading = true;
    this.loadError = null;
    // Clear any prior payment / revisions so a failed refetch can't
    // leave stale data on screen (split cap, actuals callout, etc.).
    this.payment = null;
    this.revisions = [];
    this.service.getDispute(this.disputeId).subscribe({
      next: (dispute) => {
        this.dispute = dispute;
        // Load the linked payment + revisions for context. Revisions
        // are required to surface the pilot's reported actuals
        // alongside the estimate snapshot (the payment row itself
        // carries the locked ESTIMATE under deferred capture — the
        // post-acceptance "what did the pilot actually report?" data
        // lives in the actuals_reported revision row). 2026-06-11.
        this.service.getPayment(dispute.paymentId).subscribe({
          next: (payment) => {
            this.payment = payment;
            this.service.getRevisions(dispute.paymentId).subscribe({
              next: (revisions) => {
                this.revisions = revisions;
                this.loading = false;
              },
              error: (err) => {
                // Revisions fetch failure isn't fatal — render
                // dispute + payment alone (skip the actuals callout).
                this.revisions = [];
                this.loading = false;
                console.warn('[DisputeDetail] revisions fetch failed:', err);
              },
            });
          },
          error: (err) => {
            // Payment fetch failure isn't fatal — render dispute alone.
            this.payment = null;
            this.loading = false;
            console.warn('[DisputeDetail] payment fetch failed:', err);
          },
        });
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err?.error?.error || err?.message || 'Failed to load this dispute.';
      },
    });
  }

  /**
   * The most-recent `actuals_reported` revision on the linked payment.
   * Null when the pilot hasn't reported yet OR revisions failed to
   * load. The dispute-detail page surfaces this in the "Linked
   * payment" card so the admin sees estimate vs reported actuals
   * without bouncing to the payment-detail page.
   */
  get latestReportedActuals(): MarketplacePaymentRevision | null {
    for (let i = this.revisions.length - 1; i >= 0; i--) {
      if (this.revisions[i].revisionType === 'actuals_reported') {
        return this.revisions[i];
      }
    }
    return null;
  }

  /**
   * True when the reported actuals differ from the payment row's
   * billable acres by more than rounding-noise tolerance.
   */
  get hasActualsDivergence(): boolean {
    const reported = this.latestReportedActuals;
    if (!reported?.billableAcres) return false;
    if (!this.payment?.billableAcres) return false;
    return Math.abs(reported.billableAcres - this.payment.billableAcres) > 0.005;
  }

  /**
   * Total charge that would land if admin captures the actuals as
   * reported by the pilot. Prefers the revision row's recomputed
   * snapshot when populated (post-fix path — bid-service.ts +
   * job-service.ts 2026-06-11 changes). Falls back to computing
   * `acres × rate × (1 + farmer_surcharge_pct)` inline when the
   * revision row is from before the fix and has NULL fee fields —
   * those old rows still carry the billable_acres + price_per_acre.
   *
   * Surfaced under the Pilot wins resolution as "Approve to capture
   * $X" so the admin can see at a glance what they'd charge before
   * needing to bounce to the payment-detail Adjust modal.
   */
  get actualsRecomputedTotal(): number | null {
    const reported = this.latestReportedActuals;
    if (!reported) return null;
    if (reported.totalChargeAmount != null) {
      return reported.totalChargeAmount;
    }
    // Legacy / pre-fix revision row — synthesize from acres × rate.
    // The 5% farmer surcharge is the only fee applied to the farmer's
    // total. See [marketplace-fees.ts] for the canonical math.
    if (reported.billableAcres != null && reported.pricePerAcre != null) {
      const gross = reported.billableAcres * reported.pricePerAcre;
      return Math.round(gross * 1.05 * 100) / 100;
    }
    return null;
  }

  /**
   * Dollar delta between the recomputed actuals total and the
   * estimate currently on the payment row. Returns null when either
   * side can't be computed; null when the totals match exactly.
   */
  get actualsRecomputedDelta(): number | null {
    const recomputed = this.actualsRecomputedTotal;
    if (recomputed == null || !this.payment) return null;
    const delta = recomputed - this.payment.totalChargeAmount;
    if (Math.abs(delta) < 0.005) return null;
    return delta;
  }

  get isOpen(): boolean {
    return this.dispute?.status === 'open' ||
           this.dispute?.status === 'evidence_requested' ||
           this.dispute?.status === 'under_review';
  }

  // True when the linked payment was never captured — i.e. the dispute
  // was filed BEFORE admin approval (status went `agreed_pending` /
  // `pending_admin_approval` → `disputed` without any Stripe charge).
  //
  // Drives THREE UI choices:
  //   1. Info banner above the Resolve section explaining the
  //      pre-capture flow.
  //   2. Different copy on the Farmer wins / Pilot wins radios
  //      (cancellation vs restore-to-pending-approval framing).
  //   3. Hides the Split radio entirely — Hub rejects pre-capture
  //      split with INVALID_RESOLUTION pointing to "use Pilot wins,
  //      then Adjust before approving."
  //
  // Resolution paths fully wired end-to-end 2026-06-11 (Hub:
  // DisputeService.resolve branches on `!payment.stripePaymentIntentId`,
  // MarketplacePaymentService.cancelFromPreCaptureDispute +
  // restoreToPendingApprovalFromDispute; tests in
  // dispute-service.test.ts).
  get isPreCaptureDispute(): boolean {
    return !!this.payment && this.payment.capturedAt == null;
  }

  get resolveDisabled(): boolean {
    if (this.processing) return true;
    if (!this.resolutionType) return true;
    if (this.resolutionType === 'split') {
      if (
        this.refundAmount == null ||
        !Number.isFinite(this.refundAmount) ||
        this.refundAmount <= 0
      ) return true;
      // The linked payment is fetched best-effort (reload tolerates its
      // failure), but a split MUST be bounded against the captured total
      // — without it there's no client-side cap. Block split until the
      // payment loads; farmer_wins / pilot_wins don't need it, so they
      // stay enabled. `>=` matches the Hub rule (a full-charge refund is
      // a farmer_wins, not a split).
      if (!this.payment) return true;
      if (this.refundAmount >= this.payment.totalChargeAmount) return true;
    }
    return false;
  }

  resolve(): void {
    if (!this.dispute || !this.resolutionType) return;
    this.processing = true;
    this.submitError = null;
    this.service
      .resolveDispute(this.dispute.id, {
        resolutionType: this.resolutionType,
        refundAmount: this.resolutionType === 'split' ? this.refundAmount ?? undefined : undefined,
        resolutionNotes: this.resolutionNotes.trim() || undefined,
      })
      .subscribe({
        next: () => {
          this.processing = false;
          this.toast.success('Dispute resolved.');
          this.reload();
        },
        error: (err) => {
          this.processing = false;
          this.submitError =
            err?.error?.error || err?.message || 'Failed to resolve dispute.';
          this.toast.error(this.submitError ?? 'Failed to resolve dispute.');
        },
      });
  }

  // ─── View helpers (mirror disputes-list) ────────────────────────

  statusBadgeClass(status: string): string {
    if (status === 'open') return 'badge bg-warning text-dark';
    if (status === 'resolved_farmer_wins') return 'badge bg-info text-dark';
    if (status === 'resolved_pilot_wins') return 'badge bg-success';
    if (status === 'resolved_partial') return 'badge bg-info text-dark';
    return 'badge bg-secondary';
  }

  statusDisplay(status: string): string {
    const map: Record<string, string> = {
      open: 'Open',
      evidence_requested: 'Evidence requested',
      under_review: 'Under review',
      resolved_farmer_wins: 'Resolved — farmer wins',
      resolved_pilot_wins: 'Resolved — pilot wins',
      resolved_partial: 'Resolved — partial',
      closed: 'Closed',
    };
    return map[status] || status;
  }

  reasonDisplay(reason: DisputeReasonCode): string {
    const map: Record<DisputeReasonCode, string> = {
      incomplete_coverage: 'Incomplete coverage',
      poor_spray_quality: 'Poor spray quality',
      wrong_chemical: 'Wrong chemical',
      property_damage: 'Property damage',
      no_show_partial: 'No-show / partial',
      billing_error: 'Billing error',
      other: 'Other',
    };
    return map[reason] || reason;
  }

  resolutionTypeDisplay(t: string | null): string {
    if (!t) return '—';
    const m: Record<string, string> = {
      full_refund: 'Full refund (farmer wins)',
      partial_refund: 'Partial refund (split)',
      no_refund: 'No refund (pilot wins)',
      partial_split: 'Partial split',
    };
    return m[t] || t;
  }

  formatMoney(v: number | null): string {
    if (v == null) return '—';
    return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  // Matches the precision rules used on payment-detail + the mobile
  // formatAcres helper: 2 decimals, trailing zeros stripped.
  formatAcres(v: number | null): string {
    if (v == null) return '—';
    return v.toFixed(2).replace(/\.?0+$/, '') + ' ac';
  }

  formatDateTime(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}
